import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Paper
} from '@mui/material';
import { handleKakaoCallback, exchangeCodeForToken, clearUrlParameters } from '../utils/kakaoCallback';
import { apiClient } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { UserStatus } from '../types';

const KakaoCallbackPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    
    const processKakaoCallback = async () => {
      if (!isMounted) return;
      
      try {
        const { code, error: callbackError } = handleKakaoCallback();
        
        if (callbackError) {
          setError(`카카오 로그인 오류: ${callbackError}`);
          setLoading(false);
          return;
        }
        
        if (!code) {
          setError('인증 코드를 받지 못했습니다.');
          setLoading(false);
          return;
        }

        // 이미 처리된 코드인지 확인
        const processedCode = sessionStorage.getItem('processedKakaoCode');
        if (processedCode === code) {
          setError('이미 처리된 인증 코드입니다. 새로 로그인해주세요.');
          setLoading(false);
          setTimeout(() => {
            clearUrlParameters();
            navigate('/login');
          }, 2000);
          return;
        }

        // 서버로 인증 코드 전달하여 토큰 교환
        const response = await exchangeCodeForToken(code);
        
        console.log('서버 응답:', response);

        // 서버에서 JWT 토큰과 사용자 정보를 받음
        if (response.result?.tokenDto?.accessToken) {
          // 액세스 토큰만 localStorage에 저장 (refreshToken은 HttpOnly 쿠키)
          localStorage.setItem('accessToken', response.result.tokenDto.accessToken);
        }

        // 사용자 정보 확인
        const user = response.result?.userInfo;
        if (user) {
          // 인증 성공 시 로그인 처리
          login(user);

          // 처리된 코드 저장 (중복 처리 방지)
          sessionStorage.setItem('processedKakaoCode', code);

          // URL 파라미터 정리
          clearUrlParameters();

          // 사용자 상태에 따른 리다이렉트 처리
          if (isMounted) {
            // UserStatus enum을 사용한 상태 체크
            if (user.status === UserStatus.GUEST) {
              // GUEST: 프로필이 이미 존재하면서 본인인증 미완료 → 본인인증 페이지로
              const existingProfile = await apiClient.getProfile();
              if (existingProfile && !existingProfile.identityVerified) {
                setTimeout(() => navigate('/identity-verification'), 100);
              } else {
                // 프로필 미등록 → 프로필 등록 페이지로
                const kakaoUserData = {
                  type: 'kakao',
                  userId: user.userId,
                  email: user.email,
                  name: user.userName,
                  profileImage: user.profileImageUrl || '',
                };
                sessionStorage.setItem('registrationUserData', JSON.stringify(kakaoUserData));
                setTimeout(() => navigate('/register/profile'), 100);
              }
            } else if (user.status === UserStatus.SUSPENDED || user.status === UserStatus.BANNED) {
              setTimeout(() => navigate('/sanction-block'), 100);
            } else {
              // PENDING, APPROVED, REJECTED 상태는 대시보드로 이동
              setTimeout(() => navigate('/dashboard'), 100);
            }
          }
        } else {
          throw new Error('사용자 정보를 받지 못했습니다.');
        }
      } catch (err: any) {
        console.error('카카오 콜백 처리 오류:', err);
        if (isMounted) {
          setError(err.message || '로그인 처리 중 오류가 발생했습니다.');
          setLoading(false);
        }
      }
    };

    processKakaoCallback();
    
    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 의존성 배열로 한 번만 실행

  if (loading) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              카카오 로그인 처리 중...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              잠시만 기다려주세요
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2">
              <a href="/login" style={{ color: '#1976d2', textDecoration: 'none' }}>
                로그인 페이지로 돌아가기
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return null;
};

export default KakaoCallbackPage;